import { useRef, useState } from "react";
import { UploadCloud, Loader2, CheckCircle2, Globe2, Ship, Sofa, Wallet } from "lucide-react";
import { toast } from "sonner";
import { API } from "@/lib/api";
import { Reveal } from "@/components/Reveal";
import { still } from "@/lib/media";

const TIPOS = ["Cocina", "Baño", "Clóset / Vestier", "Mobiliario a medida", "Obra / remodelación completa", "Otro"];
const PERKS = [
    { icon: Globe2, t: "Diseño internacional", d: "Aliados de fabricación en el exterior" },
    { icon: Sofa, t: "Proyecto llave en mano", d: "Desde recubrimientos hasta el mobiliario" },
    { icon: Wallet, t: "Fracción del precio", d: "Muy por debajo de lo que ofrecen en LatAm" },
    { icon: Ship, t: "Gestión completa", d: "Nosotros coordinamos fabricación y envío" },
];

export const ProjectSection = () => {
    const fileRef = useRef(null);
    const [form, setForm] = useState({ nombre: "", telefono: "", email: "", ciudad: "", tipo_proyecto: TIPOS[0], descripcion: "" });
    const [file, setFile] = useState(null);
    const [loading, setLoading] = useState(false);
    const [done, setDone] = useState(null);

    const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

    const submit = async (e) => {
        e.preventDefault();
        if (!form.nombre || !form.telefono) return toast.error("Completa tu nombre y teléfono");
        setLoading(true);
        try {
            const fd = new FormData();
            Object.entries(form).forEach(([k, v]) => fd.append(k, v));
            if (file) fd.append("file", file);
            const res = await fetch(`${API}/projects`, { method: "POST", body: fd });
            if (!res.ok) throw new Error("fail");
            const data = await res.json();
            setDone(data);
            toast.success("¡Proyecto recibido!", { description: data.codigo });
        } catch {
            toast.error("No se pudo enviar. Intenta de nuevo.");
        } finally {
            setLoading(false);
        }
    };

    const inputCls = "w-full bg-white border border-linea focus:border-madera rounded-xl outline-none py-3 px-4 text-ink transition-colors";

    return (
        <section id="proyecto" className="relative hero-gradient text-white overflow-hidden">
            <img src={still("eligiendo")} alt="" className="absolute inset-0 w-full h-full object-cover opacity-10" />
            <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24 grid lg:grid-cols-12 gap-10 lg:gap-16 items-start">
                {/* Pitch */}
                <div className="lg:col-span-5">
                    <Reveal>
                        <span className="text-xs uppercase tracking-[0.2em] font-semibold text-madera">Diseño + fabricación internacional</span>
                        <h2 className="font-display font-extrabold text-3xl md:text-5xl mt-2 tracking-tight leading-tight">Compártenos tu proyecto</h2>
                        <p className="text-white/75 mt-4 leading-relaxed max-w-lg">
                            Sube tu plano o idea y lo desarrollamos con nuestros aliados de fabricación internacional en un proyecto completo —
                            recubrimientos, acabados y mobiliario— a una fracción del precio de Latinoamérica.
                        </p>
                    </Reveal>
                    <div className="mt-8 space-y-4">
                        {PERKS.map((p, i) => (
                            <Reveal key={p.t} delay={i * 0.06}>
                                <div className="flex gap-4 items-start">
                                    <div className="h-11 w-11 rounded-full bg-white/10 flex items-center justify-center shrink-0">
                                        <p.icon size={20} className="text-madera" />
                                    </div>
                                    <div>
                                        <p className="font-display font-semibold text-lg leading-tight">{p.t}</p>
                                        <p className="text-white/60 text-sm">{p.d}</p>
                                    </div>
                                </div>
                            </Reveal>
                        ))}
                    </div>
                </div>

                {/* Form */}
                <div className="lg:col-span-6 lg:col-start-7">
                    <Reveal delay={0.1}>
                        <div className="bg-hueso text-ink rounded-2xl p-6 md:p-8 shadow-lift">
                            {done ? (
                                <div className="text-center py-10" data-testid="project-success">
                                    <CheckCircle2 size={64} className="text-verde mx-auto" strokeWidth={1.5} />
                                    <h3 className="font-display font-bold text-2xl mt-4">¡Recibido!</h3>
                                    <p className="text-mut mt-2">Tu proyecto <span className="font-bold text-naranja">{done.codigo}</span> fue enviado. Te contactaremos pronto para los siguientes pasos.</p>
                                    <button onClick={() => { setDone(null); setForm({ nombre: "", telefono: "", email: "", ciudad: "", tipo_proyecto: TIPOS[0], descripcion: "" }); setFile(null); }} className="mt-6 rounded-full border border-verde text-verde px-6 py-3 font-medium hover:bg-verde hover:text-white transition-colors">
                                        Enviar otro proyecto
                                    </button>
                                </div>
                            ) : (
                                <form onSubmit={submit} className="space-y-4">
                                    <h3 className="font-display font-bold text-xl">Cuéntanos qué quieres construir</h3>
                                    <div className="grid sm:grid-cols-2 gap-4">
                                        <input data-testid="proj-nombre" className={inputCls} placeholder="Nombre *" value={form.nombre} onChange={set("nombre")} />
                                        <input data-testid="proj-telefono" className={inputCls} placeholder="Teléfono / WhatsApp *" value={form.telefono} onChange={set("telefono")} />
                                        <input data-testid="proj-email" className={inputCls} placeholder="Correo" value={form.email} onChange={set("email")} />
                                        <input data-testid="proj-ciudad" className={inputCls} placeholder="Ciudad" value={form.ciudad} onChange={set("ciudad")} />
                                    </div>
                                    <select data-testid="proj-tipo" className={inputCls} value={form.tipo_proyecto} onChange={set("tipo_proyecto")}>
                                        {TIPOS.map((t) => <option key={t} value={t}>{t}</option>)}
                                    </select>
                                    <textarea data-testid="proj-descripcion" className={`${inputCls} min-h-24`} placeholder="Describe tu proyecto: medidas, estilo, referencias…" value={form.descripcion} onChange={set("descripcion")} />

                                    <input ref={fileRef} type="file" data-testid="proj-file" accept=".pdf,.jpg,.jpeg,.png,.webp,.dwg,.dxf,.zip,.rar,.doc,.docx" className="hidden" onChange={(e) => setFile(e.target.files[0] || null)} />
                                    <button type="button" onClick={() => fileRef.current?.click()} data-testid="proj-file-btn" className="w-full border-2 border-dashed border-linea rounded-xl py-6 flex flex-col items-center gap-2 text-mut hover:border-madera hover:text-madera transition-colors">
                                        <UploadCloud size={28} strokeWidth={1.5} />
                                        <span className="text-sm font-medium">{file ? file.name : "Sube tu plano (PDF, imagen, DWG…)"}</span>
                                        <span className="text-[11px] opacity-70">Máx. 25 MB</span>
                                    </button>

                                    <button type="submit" disabled={loading} data-testid="proj-submit" className="w-full bg-naranja text-white rounded-full py-4 font-semibold hover:bg-naranja-dark transition-colors flex items-center justify-center gap-2 disabled:opacity-60">
                                        {loading ? <><Loader2 size={16} className="animate-spin" /> Enviando…</> : "Enviar mi proyecto"}
                                    </button>
                                </form>
                            )}
                        </div>
                    </Reveal>
                </div>
            </div>
        </section>
    );
};
